/**
 * Height of the sticky landing header, so a scrolled-to section clears it rather
 * than hiding behind it. Mirrors the offset DealRoomLanding uses for top-level
 * sections; kept here so child accordions scroll identically.
 */
const STICKY_HEADER_OFFSET_MOBILE = 140;
const STICKY_HEADER_OFFSET_DESKTOP = 120;
const MOBILE_MAX_WIDTH = 600;

const POLL_MS = 100;
/** Drift worth acting on; below this a correction would only cause jitter. */
const TOLERANCE_PX = 8;
/** The page counts as ready once its height holds still for this long. */
const QUIET_FOR_MS = 300;
/** Cap on waiting for quiet, so a page that never stops still gets its scroll. */
const MAX_WAIT_MS = 6000;
/**
 * Absolute deadline for the whole attempt. A hash can name a section this room
 * does not have, and without this the watch would wait for an element that is
 * never coming — and anything waiting on it would wait forever too.
 */
const GIVE_UP_AFTER_MS = 15000;
/** How long to keep the target in place afterwards, as the rest loads in. */
const HOLD_FOR_MS = 5000;
/**
 * Re-aims allowed while each one is still closing the gap. A jump can be cut
 * short because the page is not yet tall enough to put the target at the top;
 * the panels it reveals then render, the page grows, and the next jump gets
 * closer. Stopping at a fixed count leaves the reader stranded part-way.
 */
const MAX_JUMPS = 12;
/** A jump must close at least this much of the gap to count as progress. */
const PROGRESS_PX = 24;
/** Jumps shorter than this many screens animate; longer ones go straight there. */
const SMOOTH_WITHIN_SCREENS = 1.5;

/**
 * Real reader intent. `scroll` is unusable here: our own scrolling fires it
 * too, which would cancel the watch the moment it started working.
 */
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

/**
 * Only one section is ever targeted. A deep-linked child and its parent ask at
 * almost the same moment; without this they fight and settle differently on
 * each load, which is what made the landing look random.
 */
let cancelActiveWatch: (() => void) | null = null;

const headerOffset = () =>
  window.innerWidth < MOBILE_MAX_WIDTH ? STICKY_HEADER_OFFSET_MOBILE : STICKY_HEADER_OFFSET_DESKTOP;

/**
 * Scroll the section/panel with `id` so its top sits just below the sticky
 * header, once the page has finished settling, and keep it there.
 *
 * The landing waits deliberately. Panels render their PDF only once they come
 * near the viewport, so scrolling in steps drags each one into view on the way
 * past, every one of them grows, and the target keeps moving — the reader sees
 * the page arrive somewhere, then leave again. Going straight there in one
 * move leaves those panels above the viewport, where they are never shown and
 * so never resize, and the position stays correct.
 *
 * After a click the page is already quiet, so the wait ends on the first tick
 * and navigation stays immediate.
 */
export const scrollToSection = (id: string, onSettled?: () => void): void => {
  if (!id) {
    onSettled?.();
    return;
  }

  cancelActiveWatch?.();

  const startedAt = Date.now();
  let lastHeight = -1;
  let quietFor = 0;
  let jumpedAt = 0;
  let jumps = 0;
  let lastGap = Number.POSITIVE_INFINITY;
  let lastY = -1;
  let anchorTop = 0;
  let timer = 0;

  let settled = false;
  /** Fires once, whether the target was reached or the attempt was abandoned. */
  const settle = () => {
    if (settled) return;
    settled = true;
    onSettled?.();
  };

  const stop = () => {
    window.clearInterval(timer);
    USER_INPUT_EVENTS.forEach((event) => window.removeEventListener(event, stop));
    if (cancelActiveWatch === stop) cancelActiveWatch = null;
    settle();
  };

  cancelActiveWatch = stop;
  USER_INPUT_EVENTS.forEach((event) => window.addEventListener(event, stop, { passive: true }));

  timer = window.setInterval(() => {
    if (!jumpedAt && Date.now() - startedAt >= GIVE_UP_AFTER_MS) {
      stop();
      return;
    }

    // Re-read each tick — the section may still be mounting.
    const el = document.getElementById(id);
    if (!el) return;

    const top = el.getBoundingClientRect().top;

    if (jumpedAt) {
      // Hold: cancel out anything that still moves above the target, exactly
      // and instantly, so it does not drift once the reader is looking at it.
      const drift = top - anchorTop;
      if (Math.abs(drift) > TOLERANCE_PX) window.scrollBy({ top: drift, behavior: 'auto' });
      if (Date.now() - jumpedAt >= HOLD_FOR_MS) stop();
      return;
    }

    const height = document.documentElement.scrollHeight;
    const quiet = height === lastHeight && document.readyState === 'complete';
    lastHeight = height;
    quietFor = quiet ? quietFor + POLL_MS : 0;

    if (quietFor < QUIET_FOR_MS && Date.now() - startedAt < MAX_WAIT_MS) return;

    const offset = headerOffset();
    if (Math.abs(top - offset) <= TOLERANCE_PX) {
      // Arrived — from here on the target is only held, never re-aimed.
      jumpedAt = Date.now();
      anchorTop = offset;
      // Arrived: the reader can be shown the page now. Holding carries on
      // quietly underneath, but there is nothing left to wait for.
      settle();
      return;
    }

    const y = window.pageYOffset;
    if (y !== lastY) {
      // A smooth jump is still travelling; anchoring now would fight it.
      lastY = y;
      return;
    }
    lastY = y;

    // Keep aiming only while it is working. Each jump that fails to close the
    // gap means the page cannot place the target any better than it already
    // has, and aiming again would only shuffle the reader around.
    const gap = Math.abs(top - offset);
    const closed = lastGap - gap;
    if (jumps > 0 && closed < PROGRESS_PX) {
      stop();
      return;
    }
    lastGap = gap;

    if (jumps >= MAX_JUMPS) {
      stop();
      return;
    }

    jumps += 1;
    // Long jumps go straight there; a glide would itself drag everything in
    // between into view. Short hops animate, where the motion reads as movement.
    const isNearby = Math.abs(top - offset) <= window.innerHeight * SMOOTH_WITHIN_SCREENS;
    window.scrollTo({ top: top + y - offset, behavior: isNearby ? 'smooth' : 'auto' });
  }, POLL_MS);
};
